import type { HoldingsDto } from "./useHoldings";
import type { InfomeldingerDto } from "./useInfomeldinger";
import type { BeneficiaryDto, F2100PolicyDetailsDto, Kapitalforvalter, NjordPolicyDetalilsDto } from "./usePolicyDetails";
import type { PoliseOversiktDto } from "./usePoliseOversikt";
import type { PrognoseForutsetningerDto } from "./usePrognoseForutsetninger";
import type { ForvaltningsForm } from "../components/investeringsvalg/forvaltningsformer";
import type { Spareprofil } from "../components/investeringsvalg/spareprofiler";
import type { Prognose } from "../components/kartlegging/oversikt";
import type { EPKSamtykker } from "../dispatchers/sl";
import type { Transaction } from "../reducers/accountTransactions";
import type { BankAccounts } from "../reducers/bankAccounts";
import type { Dokument } from "../reducers/documents";
import type { OpenInvestmentFund } from "../reducers/investmentPrograms";
import type { Balance } from "../reducers/kartlegging";
import type { Policy, ArchivedRecommendation } from "../reducers/policies";
import type { Samtykke } from "../reducers/samtykke";
import type { FlyttPensjonAvtaleBestilling } from "~/flytt-privat-pensjon/bestilling-bekreftelse/hooks/useSendFlytteBestilling";
import type { FlytteBestillingResponse } from "~/flytt-privat-pensjon/felles/hooks/usePrivatPensjonBestillinger";
import type { EPK, FlyttEpk, LagreEPKTilbud, SlAvtaleType } from "~/flytt-sl/felles/hooks/useFlyttEpk";
import type { Config, LoginContext } from "~/shared/api/useConfig";
import type { Customer, Fatca } from "~/shared/reducers/customer";
import type { OpprettetKrav, TypeSykdomAvsluttetArsakEnum, TypeSykdomSakStatusEnum } from "~/uforeerstatning/felles/hooks/useUforeerstatning";
import { getJson as defaultGetJson, getText as defaultGetText, del as defaultDel, post as defaultPost, put as defaultPut } from "~/utils/fetch-cors";
import type { VeiledningsSvar, VeiledningsSporsmal } from "~/utils/kartlegging";
import type { Products } from "~/utils/new-savings";
import { period as transactionPeriods } from "~/utils/policies";
import type { Period } from "~/utils/policies";
import { trackingEnabled } from "~/utils/tracking";
import type { PkbBestilling } from "~/flytt-pkb/felles/hooks/useFlyttPkb";
import type { ForvaltningPriceInfo } from "../reducers/prices";

export const SERVER_VALIDATION_ERROR = "SERVER_VALIDATION_ERROR";
export const READING_MODE_ERROR = "READING_MODE_ERROR";
export const RPM_ACCESS_ERROR = "RPM_ACCESS_ERROR";

export type PpmFunctionalErrorCode =
	| "SCHEDULED_DOWNTIME"
	| "AGREEMENT_DOES_NOT_EXIST"
	| "TRIGGER_ALREADY_ORDERED"
	| "MEDLEM_UTMELDT"
	| "FREMREGN_VENTEDE_TRANSAKSJONER"
	| "TOO_MANY_TRANSACTIONS"
	| "PRODUCT_ID_MISMATCH"
	| "GENERAL_ERROR"
	| "INVALID_EMAIL"
	| "NEW_POLICY_OFFER_EXISTS"
	| "FOR_MANGE_POSTER";

type ErrorType = "SERVER_VALIDATION_ERROR" | "READING_MODE_ERROR" | "RPM_ACCESS_ERROR";

export class ErrorWithType extends Error {
	type: ErrorType;

	constructor(message: string, type: ErrorType) {
		super(message);
		this.type = type;
	}
}

export let apiRoot = "/privat/forsikring/pensjon/web/api";

export const checkedRequest =
	(method) =>
	async (...args) => {
		try {
			const response = await method(...args);
			return response;
		} catch (e) {
			if (e.status === 400 && e.response.headers.get("x-ppm-field-error")) {
				throw new ErrorWithType(e.response.headers.get("x-ppm-field-error"), "SERVER_VALIDATION_ERROR");
			}
			if (e.status === 400 && e.response.headers.get("x-ppm-reading-mode")) {
				throw new ErrorWithType("violated reading mode", "READING_MODE_ERROR");
			}
			if (e.status === 403 && e.response.headers.get("x-ppm-no-access") === "No RPM access") {
				throw new ErrorWithType("RPM access error", "RPM_ACCESS_ERROR");
			}
			throw e;
		}
	};

export const getJson = checkedRequest(defaultGetJson);
export const getText = checkedRequest(defaultGetText);
export const post = checkedRequest(defaultPost);
export const put = checkedRequest(defaultPut);
export const del = checkedRequest(defaultDel);

export function initialize(host = "") {
	apiRoot = `${host}${apiRoot}`;
}

export function radgiverLogin(data) {
	return post(`${apiRoot}/radgiver/login`, data);
}

export function loginDemo(age: string, radgiver: boolean, hasEpkTilbud: boolean, isLo: boolean, isSlKandidat: boolean, isNyKunde: boolean) {
	if (radgiver) {
		return post(`${apiRoot}/demo/radgiver/${age}?hasEpkTilbud=${hasEpkTilbud}&isLo=${isLo}&isNyKunde=${isNyKunde}`);
	}
	return post(`${apiRoot}/demo/${age}?hasEpkTilbud=${hasEpkTilbud}&isLo=${isLo}&isSlKandidat=${isSlKandidat}`);
}

export type InternalPoliciesMessage = "interne" | "distributør" | "flere_banker" | null;

export function showInternalPoliciesMessage(): Promise<InternalPoliciesMessage> {
	return getText(`${apiRoot}/radgiver/sb1-internal-policies-message`);
}

export function postError(stack, componentStack, actions) {
	if (process.env.NODE_ENV === "test") {
		return Promise.resolve();
	}

	const payload = {
		componentStack: componentStack.replace(/\s+/g, " ").replace(/\$/g, "-"),
		stack: stack.replace(/\s+/g, " ").replace(/\$/g, "-").replace(/~/g, "-"),
		url: window.location.href,
		actions: actions.map((a) => a.type),
	};

	// The backend has a hard limit of 4000 Bytes on the allowed content-length but the
	// event log can potentially be quite long so keep trimming it untill it is short enough,
	// starting with purging the oldest events first.
	while (JSON.stringify(payload).length > 4000) {
		payload.actions.splice(0, 1);
	}

	return post(`${apiRoot}/log/error`, payload);
}

interface LogErrorDto {
	url: string;
	message: string;
	mappedStack: string;
}
export function logError(error: LogErrorDto) {
	return post(`${apiRoot}/log/error`, error);
}

export function postInfo(infoMessage, data?) {
	post(`${apiRoot}/log/info`, {
		infoMessage,
		data,
	});
}

export function track(id: string, action: string, data: any = undefined): Promise<void> {
	if (trackingEnabled()) {
		return post(`${apiRoot}/tracking`, {
			id,
			action,
			data,
		});
	}
	return Promise.resolve();
}

export interface FeedbackVoteRequest {
	id: string;
	positive: boolean;
	feedback: string;
	extraInfo: Record<string, string>;
}

export function vote(data: FeedbackVoteRequest) {
	return post(`${apiRoot}/feedback/vote`, data);
}

export function cancelSl(sendEpost: boolean): Promise<void> {
	return del(`${apiRoot}/sl/${sendEpost}`);
}

export function createSl(data: FlyttEpk): Promise<void> {
	return post(`${apiRoot}/sl`, data);
}

export function hentIssSb1SelvvalgtLeverandor(): Promise<boolean> {
	return getJson(`${apiRoot}/sl/selvvalgt-leverandor`);
}

interface Pensjonsleverandor {
	navn: string;
	sb1: boolean;
}
export interface FindSlAvtalerResponse {
	avtaler: SlAvtaleType[];
	selvvalgtLeverandor: Pensjonsleverandor;
}
export function findSlAvtaler(data: EPKSamtykker): Promise<FindSlAvtalerResponse> {
	return post(`${apiRoot}/sl/avtaler`, data);
}

export interface InternFindSlAvtalerRequest {
	email: string;
	loSamtykke: boolean;
}
export function internFindSlAvtaler(data): Promise<FindSlAvtalerResponse> {
	return post(`${apiRoot}/radgiver-epk/tilbud/intern-avtaler`, data);
}

export function getOkonomiSporsmal() {
	return getJson(`${apiRoot}/risk/okonomi-sporsmal`);
}

export function hentTilbud(): Promise<EPK> {
	return getJson(`${apiRoot}/radgiver-epk/tilbud`);
}

export function lagreTilbud(tilbud: LagreEPKTilbud): Promise<void> {
	return post(`${apiRoot}/radgiver-epk/tilbud`, tilbud);
}

export function slettTilbud(): Promise<void> {
	return del(`${apiRoot}/radgiver-epk/tilbud`);
}

export function getRisikovurdering(age?: number) {
	return getJson(`${apiRoot}/risk/sporsmal`, { age });
}

export function getRisikomapping(productId: string, age?: number) {
	return getJson(`${apiRoot}/risk/mapping`, { productId, age });
}

export function getBaerekraftSporsmal(): Promise<VeiledningsSporsmal[]> {
	return getJson(`${apiRoot}/risk/baerekraft-sporsmal`);
}

export function getBaerekraftMapping() {
	return getJson(`${apiRoot}/risk/baerekraft-mapping`);
}

export function archiveRisikovurdering(policyNumber: number, data: VeiledningsSvar[]): Promise<void> {
	return post(`${apiRoot}/risk/archive/${policyNumber}`, data);
}

export function saveRecommendation(policyNumber, data) {
	return post(`${apiRoot}/risk/archived-recommendation/${policyNumber}`, data);
}

export function paminnelseFornoyd() {
	return post(`${apiRoot}/risk/paminnelse/fornoyd`);
}

export function visitFondsskolen(policyNumber) {
	return put(`${apiRoot}/risk/visit-fondsskolen/${policyNumber}`);
}

export function isLoMemberWithIndeksForvaltning() {
	return getJson(`${apiRoot}/customer/lo-indeks`);
}

export function isLoMember(): Promise<boolean> {
	return getJson(`${apiRoot}/customer/is-lo`);
}

export function updateLoConsent(data) {
	return post(`${apiRoot}/samtykke/lo/update-samtykke`, data);
}

export function getLoConsentText(data) {
	const { sprak } = data;
	return getJson(`${apiRoot}/samtykke/lo/samtykke-tekst${sprak ? `?sprak=${sprak}` : ""}`);
}

export function getLoConsent() {
	return getJson(`${apiRoot}/samtykke/lo/medlem-samtykke`);
}

export function isLoMemberWithConsent() {
	return getJson(`${apiRoot}/samtykke/lo/medlem-med-samtykke`);
}

export function getLoForbund() {
	return getJson(`${apiRoot}/samtykke/lo/forbund`);
}

export interface StartUtbetalingRequest {
	startMonth: number;
	accountNumber: string;
	policyNumber: number;
	utbetalingDuration: number;
	avoidAvkorting?: boolean;
}

export interface StartUtbetalingResponse {
	startDate: number;
	invalidBankAccount: boolean;
	mustTerminatePayments: boolean;
	mustHandleManually: boolean;
}

export function startUtbetaling(data: StartUtbetalingRequest): Promise<StartUtbetalingResponse> {
	return post(`${apiRoot}/utbetaling-order/start`, data);
}

type Type = "RECURRING" | "ONETIME";
export interface StartFlexibleUtbetalingRequest extends Omit<StartUtbetalingRequest, "startMonth" | "utbetalingDuration"> {
	startMonth?: number;
	utbetalingDuration?: number;
	oneTimeAmount?: number;
	terminate?: boolean;
	type: Type;
}

export function startFlexibleUtbetaling(data: StartFlexibleUtbetalingRequest): Promise<StartUtbetalingResponse> {
	return post(`${apiRoot}/utbetaling-order/flexible/start`, data);
}

export function startLivrenteUtbetaling(data: StartFlexibleUtbetalingRequest): Promise<StartUtbetalingResponse> {
	return post(`${apiRoot}/utbetaling-order/livrente/start`, data);
}

export function deleteUtbetalingOrder(policyNumber: number): Promise<void> {
	return del(`${apiRoot}/utbetaling-order/${policyNumber}`);
}

export interface FullPrognose {
	prognosisAge: number;
	utbetalingDuration: number;
	engangsutbetaling: boolean;
	livsvarig: boolean;
	saldoNaverdi: Balance[];
	utbetaling: Balance[];
	avkastningAksjerPros: number;
	avkastningBankkontoPros: number;
	inflasjonPros: number;
}

export interface PolisePrognoseRequest {
	polisenummer: number;
	utbetalingDuration?: number;
	prognosisAge?: number;
}

export function getPolicyPrognosis({ utbetalingDuration, polisenummer, prognosisAge }: PolisePrognoseRequest): Promise<FullPrognose> {
	return getJson(`${apiRoot}/prognosis/${polisenummer}`, {
		utbetalingDuration,
		prognosisAge,
	});
}

export interface PrognosisWithAndWithoutAvkortingResponseDto {
	avkortetEngangsutbetaling: boolean;
	avkortetLivsvarig: boolean;
	avkortetYearlyAmount: number;
	avkortetUtbetalingDuration: number;
	fullPeriodLivsvarig: boolean;
	fullPeriodYearlyAmount: number;
	fullPeriodUtbetalingDuration: number;
}

export function getPolicyPrognosisWithAndWithoutAvkorting(policyNumber: number, prognosisAge: number): Promise<PrognosisWithAndWithoutAvkortingResponseDto> {
	return getJson(`${apiRoot}/prognosis/with-and-without-avkorting/${policyNumber}`, { prognosisAge });
}

export function getNewPolicyPrognosis(
	age: number,
	savingAmount: number,
	oneTimeAmount: number,
	product: string,
	spareprofil: Spareprofil,
	forvaltning: ForvaltningsForm,
	loProduct: boolean,
	prognosisAge?: number,
): Promise<Prognose> {
	return getJson(`${apiRoot}/prognosis/new-savings`, {
		age,
		savingAmount,
		oneTimeAmount,
		product,
		spareprofil,
		forvaltning,
		loProduct,
		prognosisAge,
	});
}

export interface NewPolicySparebelopRequest {
	arslonn: number;
	pensjonsniva: number;
	spareFra?: number;
	spareTil?: number;
	age: number;
	product: string;
	spareprofil: Spareprofil;
	forvaltning: ForvaltningsForm;
	loProduct: boolean;
}

interface NewPolicySparebelopResponse {
	sparebelop: number;
	nodvendigSparebelop: number;
	pensjonsniva: number;
}

export function getNewPolicySparebelop(data: NewPolicySparebelopRequest): Promise<NewPolicySparebelopResponse> {
	return post(`${apiRoot}/prognosis/new-savings/sparebelop`, data);
}

export function getNewPrognosis(policyNumber: number, spareprofil: Spareprofil, forvaltning: ForvaltningsForm): Promise<Prognose> {
	return getJson(`${apiRoot}/prognosis/new-prognosis/${policyNumber}`, { spareprofil, forvaltning });
}

export interface UpdateSavingAmountResponseDto {
	manualStepsRequired: boolean;
	errorCode: PpmFunctionalErrorCode;
}

export function setSavingAmount(policyNumber: number, savingAmount: number): Promise<UpdateSavingAmountResponseDto> {
	return post(`${apiRoot}/policy/savingamount`, { policyNumber, savingAmount });
}

export function stopSavings(policyNumber) {
	return post(`${apiRoot}/policy/stop-savings`, { policyNumber });
}

export function getPolicies(): Promise<PoliseOversiktDto> {
	return getJson(`${apiRoot}/policy-view`);
}

export function getPolicy(policyNumber: number): Promise<F2100PolicyDetailsDto> {
	return getJson(`${apiRoot}/policy/${policyNumber}`);
}

export function getNjordPolicy(policyNumber: number): Promise<NjordPolicyDetalilsDto> {
	return getJson(`${apiRoot}/policy/njord/${policyNumber}`);
}

export function getHoldings(polisenummer: number): Promise<HoldingsDto> {
	return getJson(`${apiRoot}/holdings/${polisenummer}`);
}

export function getFictiveUtbetalingMarketValue(polisenummer: number) {
	return getJson(`${apiRoot}/holdings/${polisenummer}/fictive-funds-utbetaling`);
}

export interface SetBeneficiariesDto {
	beneficiaries: BeneficiaryDto[];
}

interface SetBeneficiariesResponseDto {
	unknownSsns: string[];
	success: boolean;
}

export function setBeneficiaries(policyNumber: number, data: SetBeneficiariesDto): Promise<SetBeneficiariesResponseDto> {
	return put(`${apiRoot}/policy/${policyNumber}/beneficiaries`, data);
}

export function getPolicyRiskAssessment(policyNumber: Policy["polisenummer"]): Promise<ArchivedRecommendation> {
	return getJson(`${apiRoot}/policy/saved-recommendation/${policyNumber}`);
}

export function confirmNewPolicy(data) {
	return post(`${apiRoot}/new-policy/confirm`, data);
}

export function getCustomer(): Promise<Customer> {
	return getJson(`${apiRoot}/customer`).then((customer) =>
		Object.assign(customer, {
			dateOfBirth: new Date(customer.dateOfBirth),
		}),
	);
}

export function getConfig(): Promise<Config> {
	return getJson(`${apiRoot}/config`);
}

interface RadgiverDemoLogoutRequest {
	isDemo?: boolean;
	isRadgiver?: boolean;
}

export function logout(opts: Partial<RadgiverDemoLogoutRequest> = {}) {
	const { isDemo, isRadgiver } = opts;
	if (isRadgiver) {
		return post(`${apiRoot}${isDemo ? "/demo" : ""}/radgiver/logout`);
	}
	return post(`${apiRoot}/customer/logout`);
}

export function getZipName(zipCode) {
	return getJson(`${apiRoot}/address/zipName/${zipCode}`).then((zip) => (zip ? zip.zipName : null));
}

export interface NorskPensjonRequest {
	yearlySalary: number;
	samtykke: boolean;
	startAge: number;
	inntekt?: boolean;
	afp?: boolean;
	delvis?: boolean;
	tillatUnderUtbetaling?: boolean;
	privatPensjonFlytt?: boolean;
}

export function getNorskPensjon(data: NorskPensjonRequest) {
	return post(`${apiRoot}/norskpensjon`, data);
}

interface NorskPensjonPkb {
	avtalenummer: string;
	saldo: number;
	leverandor: string;
}
interface NorskPensjonPkbResponse {
	pkbs: NorskPensjonPkb[];
	unavailable?: boolean;
	unavailableInstruments?: string[];
}
export function getNorskPensjonPkbs(): Promise<NorskPensjonPkbResponse> {
	return post(`${apiRoot}/norskpensjon/pkbs`);
}

export function getLeverandorMedProdukter() {
	return getJson(`${apiRoot}/norskpensjon/leverandorer-med-produkter`);
}

interface ManualStepsRequiredResponseDto {
	manualStepsRequired: boolean;
}

export interface UpdateInvestmentProfileDto {
	id?: string;
	spareprofil: Spareprofil;
}

export function updateInvestmentProfile(policyNumber: number, { id, spareprofil }: UpdateInvestmentProfileDto): Promise<ManualStepsRequiredResponseDto> {
	return post(`${apiRoot}/fund-switch/investmentprofile`, {
		policyNumber,
		id,
		spareprofil,
	});
}

export interface GetOpenInvestmentFunds {
	openInvestmentFunds: OpenInvestmentFund[];
}

export function getOpenInvestmentFunds(policyNumber): Promise<GetOpenInvestmentFunds> {
	return getJson(`${apiRoot}/holdings/${policyNumber}/openinvestmentfunds`);
}

export function updateKapitalforvalter(policyNumber: number, kapitalforvalter: Kapitalforvalter): Promise<void> {
	return post(`${apiRoot}/fund-switch/kapitalforvalter`, {
		policyNumber,
		kapitalforvalter,
	});
}

export function updateNedtrapping(policyNumber: number, nedtrapping: boolean): Promise<void> {
	return post(`${apiRoot}/fund-switch/nedtrapping`, {
		policyNumber,
		nedtrapping,
	});
}

export function updateForvaltning(policyNumber: number, forvaltning: ForvaltningsForm) {
	return post(`${apiRoot}/fund-switch/forvaltning`, {
		policyNumber,
		forvaltning,
	});
}

export function updateOpenInvestmentFunds(policyNumber, funds, recommendedShareProportion, shareProportion, withinRecommendation, reminder) {
	// fund name and fund url may contain characters that fails owasp validation
	const safeFunds = funds.map(({ isin, proportion }) => ({ isin, proportion }));
	if (safeFunds.some((f) => !f.isin)) {
		postInfo(`missing isin: ${funds.map((f) => `${f.fundName}-${f.isin}-${f.url}.${f.proportion}`).join(" ")}`.replace(/[^A-Za-z0-9]/g, " "));
	}

	return post(`${apiRoot}/fund-switch/openinvestmentfunds`, {
		policyNumber,
		funds: safeFunds,
		recommendedShareProportion,
		shareProportion,
		reminder,
		withinRecommendation,
	});
}

interface MarketValueAtDateDto {
	date: number;
	marketValue: number;
}
export interface HoldingHistory {
	fromDate: number;
	marketValueStart: number;
	marketValueEnd: number;
	investedAmount: number;
	charges: number;
	yield: number;
	dividends: number;
	marketValueAtDates: MarketValueAtDateDto[];
	fetchFailed: boolean;
}
export function getHoldingsHistory(polisnummer: number, period: Period): Promise<HoldingHistory> {
	return getJson(`${apiRoot}/holdings-history/${polisnummer}/summary?period=${period}`);
}

interface TransactionPeriods {
	accountTransactions: Transaction[];
	errorCode?: string;
}

export function getAccountTransactions(policyNumber: number, period: Period | number): Promise<TransactionPeriods> {
	if (Object.keys(transactionPeriods).includes(period as any)) {
		return getJson(`${apiRoot}/account-transactions/${policyNumber}?period=${period}`);
	}
	return getJson(`${apiRoot}/account-transactions/${policyNumber}?year=${period}`);
}

type FundTransactionDto = {
	fundName: string;
	tradeDate: number;
	units: number;
	amount: number;
	price: number;
	proportion: number;
};

export interface AccountTransactionDto extends Transaction {
	registrationDate: number;
	fundTransactions: FundTransactionDto[];
}

export function getAccountTransaction(policyNumber: number, transactionId: string): Promise<AccountTransactionDto> {
	return getJson(`${apiRoot}/account-transactions/${policyNumber}/details/${transactionId}`);
}

export function getLoginContext(): Promise<LoginContext> {
	return getText(`${apiRoot}/context`);
}

export function getBankContact() {
	return getJson(`${apiRoot}/context/bank-contact`);
}

export function getBankContactByDomainName(domainName) {
	return getJson(`${apiRoot}/context/bank-by-domain`, {
		domainName,
	});
}

export function getSessionStatus() {
	return getJson(`${apiRoot}/context/session-status`);
}

export function getSalaryHistory(policyNumber) {
	return getJson(`${apiRoot}/policy/${policyNumber}/salary-history`);
}

export function getBankAccounts(): Promise<BankAccounts[]> {
	return getJson(`${apiRoot}/bank-account`);
}

export function isBankAccountKARValid(accountNumber): Promise<boolean> {
	return post(`${apiRoot}/bank-account/kar`, { accountNumber });
}

export function customerCameFromOpenPages(): Promise<void> {
	return post(`${apiRoot}/session/customer-from-open-pages`);
}

export function createPkbSigningOrder(bundle): Promise<SigningOrder> {
	return post(`${apiRoot}/pkb/signingorder`, bundle);
}

export function createPkbWorkOrders(bundles: PkbBestilling) {
	return post(`${apiRoot}/pkb/workorders`, bundles);
}

export function getPkbAvtalerTilFlytting() {
	return getJson(`${apiRoot}/pkb/workorders`);
}

interface PaymentDealResponseDto {
	success: boolean;
}

export interface PaymentDealRequestDto {
	kid: string;
	savingAmount: number; // max: 30_000; min: 300;
	accountNumber: string;
	savingDayOfMonth: string;
}

export function updatePaymentDeal(data: PaymentDealRequestDto): Promise<PaymentDealResponseDto> {
	return put(`${apiRoot}/payment-deal`, data);
}

export function deletePaymentDeal(kid: string): Promise<void> {
	return del(`${apiRoot}/payment-deal/${kid}`);
}

export function createPaymentDeal(data: PaymentDealRequestDto): Promise<PaymentDealResponseDto> {
	return post(`${apiRoot}/payment-deal`, data);
}

export function updateUtbetalingStart(policyNumber, data) {
	return post(`${apiRoot}/policy/${policyNumber}/utbetaling-start-date`, data);
}

export interface DokumenterDto {
	dokumenter: Dokument[];
}
export function getDokumenter(): Promise<DokumenterDto> {
	return getJson(`${apiRoot}/document`);
}

export function getGamleDokumenter(): Promise<DokumenterDto> {
	return getJson(`${apiRoot}/document/gamle`);
}

interface HtmlDokument {
	content: string;
}
export function getHtmlDocument(documentId): Promise<HtmlDokument> {
	return getJson(`${apiRoot}/document/${documentId}/HTML`);
}

export interface UforeVedlegg {
	dato: Date;
	navn: string;
	lest: boolean;
	dokumentid: number;
	visningsnavn: string;
	opprettetAv: string;
	sideantall: number;
	dokumenttype: string;
	status: string;
	standardFiltype: {
		navn: string;
		extension: string;
		mimeType: string;
	};
	dokumenttypegruppe: string;
	nokkelord: {
		navn: string;
		verdi: string;
		dato: Date;
	}[];
	nokkelordgrupper: string[];
	lagretdato: Date;
	dokumentdato: Date;
}
export type UforeVedleggDokumentListe = Record<string, UforeVedlegg[]>;
export function getUforeerstatningDocuments(fraDato: Date): Promise<UforeVedleggDokumentListe[]> {
	return post(`${apiRoot}/document/ufore-erstatning`, fraDato);
}

export interface DokumentData {
	fildata: string;
	navn: string;
	extension: string;
}
export function getDocummentById(documentId: number): Promise<DokumentData> {
	return getJson(`${apiRoot}/document/${documentId}/fil`);
}

export function getSamtykkeTexts(): Promise<Samtykke[]> {
	return getJson(`${apiRoot}/samtykke/samtykke-texts`);
}

export function updateMarkedsforingSamtykke(markedsforingSamtykke) {
	return post(`${apiRoot}/samtykke/update-markedsforing-samtykke`, { markedsforingSamtykke });
}

export function updateDybdeSamtykke(dybdeSamtykke) {
	return post(`${apiRoot}/samtykke/update-dybde-samtykke`, { dybdeSamtykke });
}

export function updateNorskPensjonSamtykke(norskPensjonSamtykke: boolean) {
	return post(`${apiRoot}/samtykke/update-norsk-pensjon-samtykke`, { norskPensjonSamtykke });
}

export function getNorskPensjonSamtykke() {
	return getJson(`${apiRoot}/norskpensjon/samtykke`);
}

export function updateDigitaltForstevalgSamtykke(digitaltForstevalgSamtykke) {
	return post(`${apiRoot}/samtykke/update-digitalt-forstevalg-samtykke`, { digitaltForstevalgSamtykke });
}

export function getFatcaData(): Promise<Fatca> {
	return getJson(`${apiRoot}/fatca`);
}

export function setFatcaData(data) {
	return put(`${apiRoot}/fatca`, data);
}

export function getCountries() {
	return getJson(`${apiRoot}/country`);
}

type F2100StatusResponse = {
	openForReading: boolean;
};
export function getF2100Status(): Promise<F2100StatusResponse> {
	return getJson(`${apiRoot}/status/f2100`);
}

export interface NjordUtbetaling {
	brutto: number;
	netto: number;
	utbetalingDato: number;
	status: string;
	skattebelop?: number;
	skatteprosent?: number;
}
export interface NjordUtbetalingsoversiktList {
	utbetalinger: NjordUtbetaling[];
	accountNumber: string;
}

export function getNjordUtbetalingsoversikt(policyNumber): Promise<NjordUtbetalingsoversiktList> {
	return getJson(`${apiRoot}/utbetalingsoversikt/njord/${policyNumber}`);
}

export interface Utbetaling {
	utbetalingsdato: number;
	bruttoUtbetalt: number;
	nettoUtbetalt: number;
	skatteprosent: number;
	skattebelop: number;
}

export function getUtbetalingsoversikt(policyNumber: number): Promise<Utbetaling[]> {
	return getJson(`${apiRoot}/utbetalingsoversikt/${policyNumber}`);
}

export function getCustomerF2100AccountNumber() {
	return getJson(`${apiRoot}/customer/f2100-account-number`);
}

export function setCustomerF2100AccountNumber(accountNumber: string): Promise<boolean> {
	return post(`${apiRoot}/customer/f2100-account-number`, { accountNumber });
}

export function validateEmail(email) {
	return post(`${apiRoot}/customer/email/valid`, { email });
}

export function updatePhoneNumber(telefonLandKode: string, telefonnummer: string): Promise<void> {
	return post(`${apiRoot}/customer/telefon`, { telefonLandKode, telefonnummer });
}

export function updateEmail(email: string): Promise<void> {
	return post(`${apiRoot}/customer/epost`, { email });
}

export function bekreftKontaktInfo(email: string, telefonLandKode: string, telefonnummer: string): Promise<void> {
	const sanitizedPhoneNumber = telefonnummer?.replace(/\s+/g, "");
	return post(`${apiRoot}/customer/kontakt-info`, { email, telefonLandKode, telefonnummer: sanitizedPhoneNumber });
}

export function getEpkCandidates() {
	return getJson(`${apiRoot}/epk/candidates`);
}

export function confirmEpkCandidates(ids) {
	return post(`${apiRoot}/epk/confirm`, ids);
}

export type PricesDto = {
	forvaltningskost: Record<ForvaltningsForm, ForvaltningPriceInfo>;
	administrasjonskost: number;
	adminkostIGVerdi: boolean;
	takAdminkostIGVerdi: number;
	satsRentegaranti: number;
};

export function getSlPrices(lo?: boolean) {
	return getJson(`${apiRoot}/prices/sl`, { lo });
}

export function getRegularSlPrices() {
	return getJson(`${apiRoot}/prices/sl/regular`);
}

export function getPkbPrices(lo?: boolean) {
	return getJson(`${apiRoot}/prices/pkb`, { lo });
}

export function getPolicyPrices(policyNumber: number): Promise<PricesDto> {
	return getJson(`${apiRoot}/prices/policy/${policyNumber}`);
}

export function getProductPrices(productId: string, age?: number) {
	return getJson(`${apiRoot}/prices/product/${productId}${age ? `?age=${age}` : ""}`);
}

export function getFundPrices(policyNumber) {
	return getJson(`${apiRoot}/prices/funds/${policyNumber}`);
}

export function loProduktbytte() {
	return post(`${apiRoot}/policy/lo-produktbytte`);
}

type ProduktBytteResponse = "SUKKSESS" | "KAN_IKKE_BLI_LOFAVOR" | "ALLEREDE_BESTILT";

export function pkbTilLoPkbProduktbytte(policyNumber: number): Promise<ProduktBytteResponse> {
	return post(`${apiRoot}/policy/lo-produktbytte/${policyNumber}`);
}

export function loadIpsDeposit(policyNumber: number): Promise<number> {
	return getJson(`${apiRoot}/policy/ips-deposit/${policyNumber}`);
}

export interface DirectPaymentDto {
	accountNumber: string;
	amount: number;
}

export function registerDirectPayment(policyNumber: number, data: DirectPaymentDto): Promise<boolean> {
	return post(`${apiRoot}/policy/${policyNumber}/direct-payment`, data);
}

export function oppdaterSmartjustering(polisenummer: number, spareprosent: number): Promise<boolean> {
	return post(`${apiRoot}/policy/smartjustering/${polisenummer}`, { spareprosent });
}

export function stopSmartjustering(polisenummer: number): Promise<void> {
	return del(`${apiRoot}/policy/smartjustering/${polisenummer}`);
}

export interface AltinnForesporsel {
	finnesAktivAutorisasjonForesporsel: boolean;
	autorisasjonAvvistDato?: Date;
}
export function getCustomerSamtykkeNeeded(): Promise<AltinnForesporsel> {
	return getJson(`${apiRoot}/altinn/samtykkevarsel`);
}

export function createSamtykkeInitier() {
	return post(`${apiRoot}/altinn/samtykkevarsel`);
}

type SamtykkeForesporselResponse = {
	success: true;
	url: string;
	message?: string;
};
export function createSamtykkeForesporsel(callbackUrl?: string): Promise<SamtykkeForesporselResponse> {
	if (callbackUrl) {
		return post(`${apiRoot}/altinn/samtykkeforesporsel`, callbackUrl);
	}
	return post(`${apiRoot}/altinn/samtykkeforesporsel`);
}

type GetAltinnSamtykke = {
	status: string;
};
export function getAltinnSamtykke(autorisasjonskode: string): Promise<GetAltinnSamtykke> {
	return getJson(`${apiRoot}/altinn/samtykke-status/${autorisasjonskode}`);
}

export function saveSamtykkeAuthorization(token) {
	return post(`${apiRoot}/altinn/authorization`, { token });
}

export function rejectSamtykkeAuthorization() {
	return post(`${apiRoot}/altinn/authorization-reject`);
}

export interface AltinnAuthorizationInfo {
	autorisasjonKodeFinnes: boolean;
	samtykkeForesporselOpprettetDato: Date;
	forespurtFraKunde: boolean;
	foresporselSendtTilKundeDato?: Date;
	gjelderFraDato?: Date;
	gjelderTilDato?: Date;
	samtykkeAvvist: boolean;
	samtykkeAvvistDato?: Date;
}
export function getAltinnAuthorizationInfo(): Promise<AltinnAuthorizationInfo> {
	return getJson(`${apiRoot}/altinn/authorization-info`);
}

export async function getDocumentUrl(product: Products, type: "vilkaar" | "informasjon" | "informasjon_fordel"): Promise<string> {
	return getText(`${apiRoot}/avtaledokument/${product}/${type}`);
}

export interface ProductUrls {
	produkt: string;
	vilkarUrl: string;
	viktigInformasjonUrl?: string;
	nokkelinformasjonUrl?: string;
}

export async function getDocumentUrlList(products): Promise<ProductUrls[]> {
	return post(`${apiRoot}/avtaledokument/list`, products);
}

export async function getNokkelinformasjonDokument(produkt: "PENSJONSKONTO" | "FONDSKONTO"): Promise<string> {
	return getText(`${apiRoot}/avtaledokument/nokkelinformasjon/${produkt}`);
}

export async function getFinautDokument(): Promise<string> {
	return getText(`${apiRoot}/avtaledokument/finaut`);
}

export function updateRiskAssessmentReminder(value) {
	return post(`${apiRoot}/customer/risk-assessment-reminder`, value);
}

export function getDistributor(bankNr) {
	return getJson(`${apiRoot}/customer/distributor`, { bankNr });
}

export type Sprak = "NO" | "EN";

interface AnsattbrosjyreDto {
	dokumentNavn: string;
	dokumentInnhold: string;
}

export async function getAnsattbrosjyre(policyNumber: number, sprak: Sprak): Promise<AnsattbrosjyreDto> {
	return getJson(`${apiRoot}/ansattbrosjyre/${policyNumber}/${sprak}`);
}

export interface KravResponse {
	signeringUrl: string;
	signeringOrderId?: string;
}
export function lagUforeKrav(data: OpprettetKrav): Promise<KravResponse> {
	return post(`${apiRoot}/ufore/sykdom/krav`, data);
}

export function sendSigneringOrderId(signeringOrderId: string) {
	return post(`${apiRoot}/ufore/sykdom/signering-order-id`, { signeringOrderId });
}

export interface SykdomSakInfo {
	sakId: number;
	fnr: string;
	orgnr: string;
	forsteSykedato: string;
	friskmeldtDato: string;
	overordnetStatus: string;
	overordnetStatusDato: string;
	status: TypeSykdomSakStatusEnum;
	statusDato: string;
	meldtDato: string;
	sakAvsluttetDato: string;
	avsluttetArsak: TypeSykdomAvsluttetArsakEnum;
	avsluttetDato: string;
	sluttetDato: string;
	ansatt: boolean;
	epost: string;
	tlfnr: string;
	innmelderEpost: string;
	innmelderNavn: string;
}
export function getAktivSykdomSak(): Promise<SykdomSakInfo> {
	return getJson(`${apiRoot}/ufore/sykdom/aktiv-sykdomsak`);
}

export interface PdfFile {
	name: string;
	document?: File;
	error?: string;
}
export function lastOppFiler(filer): Promise<string[]> {
	return post(`${apiRoot}/ufore/sykdom/filer`, filer);
}

export function lastOppTilleggsinfo(data) {
	return post(`${apiRoot}/ufore/sykdom/tilleggsinfo`, data);
}

interface OppdaterGradSykdom {
	nyGradFraDato: string;
	nyGradTilDato: string;
	timerPerUke: string;
	arbeidsgiverNavn: string;
}
export function endreGradSykdom(grad: OppdaterGradSykdom) {
	return post(`${apiRoot}/ufore/sykdom/endre-grad`, grad);
}

export interface HentOppdatertGradSykdom {
	sakId: number;
	fraDato: string;
	tilDato: string;
	timerPerUke: string;
	arbeidsgiverNavn: string;
	mottattDato: string;
}
export function hentGradSykdom(): Promise<HentOppdatertGradSykdom[]> {
	return getJson(`${apiRoot}/ufore/sykdom/hent-endre-grad`);
}

export function createFlytteBestilling(data: FlyttPensjonAvtaleBestilling) {
	return post(`${apiRoot}/flytte-bestilling/opprett`, data);
}

export function getFlytteBestilling(): Promise<FlytteBestillingResponse[]> {
	return getJson(`${apiRoot}/flytte-bestilling/hent`);
}

export interface SigningOrder {
	id: string;
	url?: string;
}
export function createSigningOrder(order): Promise<SigningOrder> {
	return post(`${apiRoot}/flytte-bestilling/signingorder`, order);
}

export function getPrognoseForutsetninger(): Promise<PrognoseForutsetningerDto> {
	return getJson(`${apiRoot}/prognose-forutsetninger`);
}

export function getDriftsmeldinger(): Promise<string[]> {
	return getJson(`${apiRoot}/system-meldinger/driftsmeldinger`);
}

export function getInfomeldinger(): Promise<InfomeldingerDto[]> {
	return getJson(`${apiRoot}/system-meldinger/infomeldinger`);
}

export type Artikler = {
	tittel: string;
	dato: Date;
	bilde: string;
	url: string;
};

export function getArtikler(): Promise<Artikler[]> {
	return getJson(`${apiRoot}/nyhetsbanner`);
}
